import React from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import styles from "../../../components/stats_page/stats-box.module.css";
import { Emoji } from "../../../ui";
import { resolveUserEmoji } from "../../../utils/userHandle";
import type { Season, Standings, Roster, GameFrames } from "../api/types";
import { useCurrentGame } from "../hooks/useCurrentGame";
import { GAME_BOARD_LABELS, bracketPieces, rowFrameKind } from "../utils/frames";
import { fmtDay, rankLabel } from "../utils/seasonText";
import { FrameArea, FrameBracket, FrameLegend, frameRowFill } from "./FrameMarks";

// The game's board on the home page, drawn as a sibling of the AOTD Authors
// Leaderboard (same table, title and spacing; see
// components/stats_page/AOTDLeaderboard.tsx) and rendered from the top of it.
// The whole board is one link to /game.

const SHOWN = 5;

/** The small line under the title. */
export function boardSubline(season: Season): string {
  if (season.phase === "announced" || season.phase === "open") {
    const starts = `starts ${fmtDay(season.dayKeyFrom)}`;
    if (season.me?.joined) return `${starts}, you are in`;
    return season.joinOpen ? `${starts}, press to join` : starts;
  }
  if (season.phase === "active") {
    return season.dayNumber != null ? `day ${season.dayNumber} of ${season.totalDays}` : `${season.totalDays} days`;
  }
  return "final";
}

const GameBoard: React.FC = () => {
  const { season, standings, roster, frames, failed } = useCurrentGame();
  if (!season || failed) return null;
  const beforeStart = season.phase === "announced" || season.phase === "open";
  if (beforeStart ? !roster : !standings) return null;

  return (
    <Container>
      <BoardLink to="/game" data-testid="game-board" aria-label={boardLinkLabel(season)}>
        <Title>
          <span className="gb-title">The game</span>
          <span className="font-black"> {seasonYear(season)}</span>
          <Sub>{boardSubline(season)}</Sub>
        </Title>
        {beforeStart ? (
          <RosterBoard roster={roster as Roster} />
        ) : (
          <StandingsBoard standings={standings as Standings} frames={frames} />
        )}
      </BoardLink>
    </Container>
  );
};

export default GameBoard;

function seasonYear(season: Season): string {
  return season.dayKeyTo.slice(0, 4);
}

/**
 * The link's short accessible name. Without it a screen reader would read the
 * whole table (every rank, handle and total) as the link's name.
 */
export function boardLinkLabel(season: Season): string {
  const what = season.phase === "announced" || season.phase === "open" ? "players" : "standings";
  return `The game ${seasonYear(season)} ${what}, open the game page`;
}

function StandingsBoard({ standings, frames }: { standings: Standings; frames: GameFrames | null }) {
  const rows = standings.rows.slice(0, SHOWN);
  const kinds = rows.map((r) => rowFrameKind(r, frames));
  const pieces = bracketPieces(kinds, GAME_BOARD_LABELS);
  const shared = new Map<number, number>();
  standings.rows.forEach((r) => {
    if (r.rank != null) shared.set(r.rank, (shared.get(r.rank) ?? 0) + 1);
  });
  const marked = kinds.some(Boolean);

  return (
    <FrameArea $marked={marked}>
      <table className={styles.table} style={{ overflow: "visible" }}>
        <thead>
          <tr>
            <th>Rank</th>
            <th>Player</th>
            <th>Points</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={3}>nobody yet</td>
            </tr>
          ) : (
            rows.map((r, i) => {
              const piece = pieces[i];
              return (
                <tr
                  key={r.username}
                  data-testid={`game-board-row-${r.username}`}
                  data-frame={piece?.kind}
                  style={{ backgroundColor: frameRowFill(piece?.kind) }}
                >
                  <RankCell>
                    {rankLabel(r.rank, r.rank != null && (shared.get(r.rank) ?? 0) > 1)}
                    {piece ? <FrameBracket piece={piece} srText={GAME_BOARD_LABELS[piece.kind]} /> : null}
                  </RankCell>
                  <td style={{ whiteSpace: "nowrap" }}>
                    <Emoji>{resolveUserEmoji(r.emoji)}</Emoji>.{r.username}
                  </td>
                  <td>{r.total}</td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
      <FrameLegend kinds={kinds} labels={GAME_BOARD_LABELS} />
    </FrameArea>
  );
}

function RosterBoard({ roster }: { roster: Roster }) {
  const players = roster.players.slice(0, SHOWN);
  const more = roster.count - players.length;
  return (
    <FrameArea $marked={false}>
      <table className={styles.table} style={{ overflow: "visible" }}>
        <thead>
          <tr>
            <th>Joined so far</th>
          </tr>
        </thead>
        <tbody>
          {players.length === 0 ? (
            <tr>
              <td>nobody yet</td>
            </tr>
          ) : (
            players.map((p) => (
              <tr key={p.username} data-testid={`game-board-player-${p.username}`}>
                <td>
                  <Emoji>{resolveUserEmoji(p.emoji)}</Emoji>.{p.username}
                </td>
              </tr>
            ))
          )}
          {more > 0 ? (
            <tr>
              <td>and {more} more</td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </FrameArea>
  );
}

// Same as the AOTD board's TableContainer, a little tighter at the bottom since
// the AOTD board follows straight after.
const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 15px 15px 0;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 700px) {
    padding: 8px 8px 0;
  }
`;

// Same as the AOTD board's Title.
const Title = styled.div`
  font-size: 1.1rem;
  color: #222;
  text-align: center;
  font-weight: 500;
  padding: 5px;
  padding-bottom: 2px;
`;

const Sub = styled.span`
  display: block;
  font-size: 0.72rem;
  font-weight: 500;
  color: #1a1a1a;
  margin-top: 1px;
  margin-bottom: 6px;
`;

const RankCell = styled.td`
  position: relative;
`;

const BoardLink = styled(Link)`
  display: block;
  width: 100%;
  color: inherit;
  text-decoration: none;
  cursor: pointer;

  table {
    transition: transform 0.12s ease, box-shadow 0.12s ease;
  }

  &:hover .gb-title,
  &:focus-visible .gb-title {
    text-decoration: underline;
    text-underline-offset: 3px;
  }

  &:hover table {
    transform: var(--hm-press-translate);
    box-shadow: var(--hm-shadow-pressed);
  }

  &:active table {
    transform: translate(2px, 2px);
    box-shadow: var(--hm-shadow-sm-pressed);
  }

  &:focus-visible {
    outline: 2px dashed #000;
    outline-offset: 2px;
  }
`;
